import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Clock } from "lucide-react";
import { PublicNav, PublicFooter, PublicHero } from "@/components/public/PublicChrome";
import api from "@/lib/api";
import { Loading } from "@/pages/_shared";

const CAT_ACCENT = {
  "Getting Started": "#1a5fd0",
  "Personal Credit": "#059669",
  "Business Credit": "#b0862f",
  "Funding & Capital": "#0d9488",
};

function LearningMotif() {
  const rows = [["#1a5fd0", 90], ["#059669", 70], ["#b0862f", 82], ["#0d9488", 58]];
  return (
    <div className="relative hidden h-52 w-full items-center justify-center lg:flex" aria-hidden>
      <div className="absolute h-40 w-56 rounded-full bg-[#1a5fd0]/10 blur-2xl animate-float" />
      <div className="relative w-full max-w-xs space-y-2.5">
        {rows.map(([c, w], i) => (
          <div key={i} className="reveal rounded-xl border border-slate-200 bg-white/80 p-2.5 shadow-sm backdrop-blur" style={{ animationDelay: `${i * 0.1}s` }}>
            <div className="mb-1.5 h-1.5 w-10 rounded-full" style={{ background: c }} />
            <div className="h-1.5 rounded-full bg-slate-200"><div className="h-full rounded-full" style={{ width: `${w}%`, background: c, opacity: 0.35 }} /></div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Learning() {
  const [data, setData] = useState(null);
  const [active, setActive] = useState("All");

  useEffect(() => {
    let ok = true;
    api.get("/learning/articles", { params: active === "All" ? {} : { category: active } })
      .then((r) => { if (ok) setData(r.data); })
      .catch(() => { if (ok) setData({ articles: [], categories: [], counts: {} }); });
    return () => { ok = false; };
  }, [active]);

  const cats = ["All", ...(data?.categories || [])];

  return (
    <div className="min-h-screen bg-[#f7f4ec]" data-testid="learning-page">
      <PublicNav />
      <PublicHero
        eyebrow="Learning"
        title="Credit & capital,"
        accent="made clear."
        sub="Plain-English guides that turn financial complexity into confident decisions — from your first tradeline to your first SBA loan."
        visual={<LearningMotif />}
      />
      <div className="mx-auto max-w-7xl px-5 pb-16 pt-8 lg:px-8">
        <div className="flex flex-wrap gap-2.5" data-testid="learning-categories">
          {cats.map((c) => (
            <button
              key={c}
              onClick={() => setActive(c)}
              data-testid={`learning-cat-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
              className={`rounded-full px-4 py-2 text-[13px] font-semibold transition-colors ${
                active === c ? "bg-[#0f172a] text-[#d8b25a]" : "border border-slate-300 bg-white text-slate-600 hover:border-slate-400"
              }`}
            >
              {c}{c !== "All" && data?.counts?.[c] != null ? ` · ${data.counts[c]}` : ""}
            </button>
          ))}
        </div>

        {!data ? (
          <Loading />
        ) : data.articles.length === 0 ? (
          <div className="mt-10 rounded-2xl border border-dashed border-slate-300 bg-white/60 p-10 text-center text-[14px] text-slate-500">No articles in this category yet.</div>
        ) : (
          <div className="mt-8 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {data.articles.map((a) => {
              const accent = CAT_ACCENT[a.category] || "#1a5fd0";
              return (
                <Link
                  key={a.slug}
                  to={`/learning/${a.slug}`}
                  data-testid={`learning-article-${a.slug}`}
                  className="group flex flex-col rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm transition-all hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md"
                >
                  <div className="flex items-center justify-between">
                    <span className="rounded-full px-2.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider" style={{ background: `${accent}15`, color: accent }}>{a.category}</span>
                    {a.read_minutes ? <span className="flex items-center gap-1 font-mono text-[10px] text-slate-400"><Clock size={11} />{a.read_minutes} min</span> : null}
                  </div>
                  <h3 className="mt-3 font-head text-[17px] font-bold leading-snug text-slate-900">{a.title}</h3>
                  <p className="mt-2 flex-1 text-[13px] leading-relaxed text-slate-500">{a.excerpt}</p>
                  <span className="mt-4 flex items-center gap-1.5 text-[13px] font-semibold text-[#1a5fd0] transition-all group-hover:gap-2.5">Read guide <ArrowRight size={14} /></span>
                </Link>
              );
            })}
          </div>
        )}

        <div className="mt-12 flex flex-col items-start gap-4 rounded-2xl bg-[#0f172a] p-8 sm:flex-row sm:items-center sm:justify-between" data-testid="learning-cta">
          <div className="flex items-center gap-4">
            <span className="hidden h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white/10 sm:flex"><BookOpen size={22} className="text-[#d8b25a]" /></span>
            <div>
              <div className="font-serif-display text-2xl font-medium text-white">Learn it, then do it.</div>
              <p className="mt-1 text-[14px] text-slate-300">Put every guide into action inside the platform — with your AI Capital Crew helping.</p>
            </div>
          </div>
          <Link to="/register" data-testid="learning-cta-start" className="flex shrink-0 items-center gap-2 rounded-xl bg-gradient-to-r from-[#b0862f] to-[#d8b25a] px-6 py-3 text-[13px] font-semibold text-[#0f172a] active:scale-95">Get Started Free <ArrowRight size={15} /></Link>
        </div>
      </div>
      <PublicFooter />
    </div>
  );
}
